import { createApiMethod, createApiModule } from '../apiFactory';
import type {
  SortFaqContentParams,
  SortFaqContentResponse,
  SortFaqCategoryParams,
  SortFaqCategoryResponse,
  EditFaqCategoryParams,
  EditFaqCategoryResponse,
  DeleteFaqCategoryParams,
  DeleteFaqCategoryResponse,
  EditFaqContentParams,
  EditFaqContentResponse,
  DeleteFaqContentParams,
  DeleteFaqContentResponse,
  UpdateFaqContentStatusParams,
  UpdateFaqContentStatusResponse,
  UpdateFaqCategoryStatusParams,
  UpdateFaqCategoryStatusResponse,
  BEndFaqListParams,
  BEndFaqListResponse,
  GetFaqDetailParams,
  GetFaqDetailResponse
} from './types';

/**
 * 对FAQ文章进行排序
 * @param params 排序参数，包含shopDomain、currentId和nextId
 * @returns 排序结果
 */
export const sortFaqContent = async (params: SortFaqContentParams): Promise<SortFaqContentResponse> => {
  const sortMethod = createApiMethod<SortFaqContentParams, SortFaqContentResponse>(
    '/api/helpdesk/v1/sortFaqContent',
    'POST',
    'Error sorting FAQ content'
  );
  
  try {
    // 调用FAQ文章排序API
    const response = await sortMethod(params);
    return response;
  } catch (error) {
    console.error('Sort FAQ content error:', error);
    throw error;
  }
};

/**
 * 对FAQ分类进行排序
 * @param params 排序参数，包含shopDomain、currentId和nextId
 * @returns 排序结果
 */
export const sortFaqCategory = async (params: SortFaqCategoryParams): Promise<SortFaqCategoryResponse> => {
  const sortMethod = createApiMethod<SortFaqCategoryParams, SortFaqCategoryResponse>(
    '/api/helpdesk/v1/sortFaqCategory',
    'POST',
    'Error sorting FAQ category'
  );
  
  try {
    // 调用FAQ分类排序API
    const response = await sortMethod(params);
    return response;
  } catch (error) {
    console.error('Sort FAQ category error:', error);
    throw error;
  }
};

/**
 * 编辑FAQ分类
 * @param params 编辑参数，包含shopDomain、fcaId、cateIcon、content和langCode
 * @returns 编辑结果，包含分类ID
 */
export const editFaqCategory = async (params: EditFaqCategoryParams): Promise<EditFaqCategoryResponse> => {
  const editMethod = createApiMethod<EditFaqCategoryParams, EditFaqCategoryResponse>(
    '/api/helpdesk/v1/editFaqCategory',
    'POST',
    'Error editing FAQ category'
  );
  
  try {
    // 调用FAQ分类编辑API
    const response = await editMethod(params);
    return response;
  } catch (error) {
    console.error('Edit FAQ category error:', error);
    throw error;
  }
};

/**
 * 删除FAQ分类
 * @param params 删除参数，包含shopDomain和fcaId
 * @returns 删除结果
 */
export const deleteFaqCategory = async (params: DeleteFaqCategoryParams): Promise<DeleteFaqCategoryResponse> => {
  const deleteMethod = createApiMethod<DeleteFaqCategoryParams, DeleteFaqCategoryResponse>(
    '/api/helpdesk/v1/deleteFaqCategory',
    'POST',
    'Error deleting FAQ category'
  );
  
  try {
    // 调用FAQ分类删除API
    const response = await deleteMethod(params);
    return response;
  } catch (error) {
    console.error('Delete FAQ category error:', error);
    throw error;
  }
};

/**
 * 编辑FAQ文章
 * @param params 编辑参数，包含shopDomain、fcdId、cateInfo、isPopular、publishStatus、content和langCode
 * @returns 编辑结果，包含内容ID
 */
export const editFaqContent = async (params: EditFaqContentParams): Promise<EditFaqContentResponse> => {
  const editMethod = createApiMethod<EditFaqContentParams, EditFaqContentResponse>(
    '/api/helpdesk/v1/editFaqContent',
    'POST',
    'Error editing FAQ content'
  );
  
  try {
    // 调用FAQ文章编辑API
    const response = await editMethod(params);
    return response;
  } catch (error) {
    console.error('Edit FAQ content error:', error);
    throw error;
  }
};

/**
 * 删除FAQ文章
 * @param params 删除参数，包含shopDomain和fcdId
 * @returns 删除结果
 */
export const deleteFaqContent = async (params: DeleteFaqContentParams): Promise<DeleteFaqContentResponse> => {
  const deleteMethod = createApiMethod<DeleteFaqContentParams, DeleteFaqContentResponse>(
    '/api/helpdesk/v1/deleteFaqContent',
    'POST',
    'Error deleting FAQ content'
  );
  
  try {
    // 调用FAQ文章删除API
    const response = await deleteMethod(params);
    return response;
  } catch (error) {
    console.error('Delete FAQ content error:', error);
    throw error;
  }
};

/**
 * 修改FAQ文章状态
 * @param params 修改参数，包含shopDomain、fcdId、isPopular和publishStatus
 * @returns 修改结果
 */
export const updateFaqContentStatus = async (params: UpdateFaqContentStatusParams): Promise<UpdateFaqContentStatusResponse> => {
  const updateMethod = createApiMethod<UpdateFaqContentStatusParams, UpdateFaqContentStatusResponse>(
    '/api/helpdesk/v1/updateFaqContentStatus',
    'POST',
    'Error updating FAQ content status'
  );
  
  try {
    // 调用FAQ文章状态修改API
    const response = await updateMethod(params);
    return response;
  } catch (error) {
    console.error('Update FAQ content status error:', error);
    throw error;
  }
};

/**
 * 获取后台FAQ列表
 * @param params 查询参数，包含分类、是否热门、语言、搜索等
 * @returns 查询结果，包含FAQ列表
 */
export const bEndFaqList = async (params: BEndFaqListParams): Promise<BEndFaqListResponse> => {
  const listMethod = createApiMethod<BEndFaqListParams, BEndFaqListResponse>(
    '/api/helpdesk/v1/bEndFaqList',
    'POST',
    'Error fetching FAQ list'
  );
  
  try {
    // 调用后台FAQ列表查询API
    const response = await listMethod(params);
    return response;
  } catch (error) {
    console.error('Fetch FAQ list error:', error);
    throw error;
  }
};

/**
 * 获取FAQ详情
 * @param params 查询参数，包含fcdId、includeAllCate、langCode和shopDomain
 * @returns 查询结果，包含FAQ详情和分类列表
 */
export const getFaqDetail = async (params: GetFaqDetailParams): Promise<GetFaqDetailResponse> => {
  const detailMethod = createApiMethod<GetFaqDetailParams, GetFaqDetailResponse>(
    '/api/helpdesk/v1/getFaqDetail',
    'POST',
    'Error fetching FAQ detail'
  );
  
  try {
    // 调用FAQ详情查询API
    const response = await detailMethod(params);
    return response;
  } catch (error) {
    console.error('Fetch FAQ detail error:', error);
    throw error;
  }
};

/**
 * 修改FAQ分类状态
 * @param params 修改参数，包含shopDomain、fcaId和publishStatus
 * @returns 修改结果
 */
export const updateFaqCategoryStatus = async (params: UpdateFaqCategoryStatusParams): Promise<UpdateFaqCategoryStatusResponse> => {
  const updateMethod = createApiMethod<UpdateFaqCategoryStatusParams, UpdateFaqCategoryStatusResponse>(
    '/api/helpdesk/v1/updateFaqCategoryStatus',
    'POST',
    'Error updating FAQ category status'
  );
  
  try {
    // 调用FAQ分类状态修改API
    const response = await updateMethod(params);
    return response;
  } catch (error) {
    console.error('Update FAQ category status error:', error);
    throw error;
  }
};

/**
 * FAQ相关API
 */
export const faqApi = createApiModule('/api/helpdesk/v1', {
  sortFaqContent: { // FAQ文章排序
    method: 'POST',
    urlSuffix: '/sortFaqContent',
    errorMessage: 'Error sorting FAQ content'
  },
  sortFaqCategory: { // FAQ分类排序
    method: 'POST',
    urlSuffix: '/sortFaqCategory',
    errorMessage: 'Error sorting FAQ category'
  },
  editFaqCategory: { // FAQ分类编辑
    method: 'POST',
    urlSuffix: '/editFaqCategory',
    errorMessage: 'Error editing FAQ category'
  },
  deleteFaqCategory: { // FAQ分类删除
    method: 'POST',
    urlSuffix: '/deleteFaqCategory',
    errorMessage: 'Error deleting FAQ category'
  },
  editFaqContent: { // FAQ文章编辑
    method: 'POST',
    urlSuffix: '/editFaqContent',
    errorMessage: 'Error editing FAQ content'
  },
  deleteFaqContent: { // FAQ文章删除
    method: 'POST',
    urlSuffix: '/deleteFaqContent',
    errorMessage: 'Error deleting FAQ content'
  },
  updateFaqContentStatus: { // FAQ文章状态修改
    method: 'POST',
    urlSuffix: '/updateFaqContentStatus',
    errorMessage: 'Error updating FAQ content status'
  },
  bEndFaqList: { // 后台FAQ列表
    method: 'POST',
    urlSuffix: '/bEndFaqList',
    errorMessage: 'Error fetching FAQ list'
  },
  getFaqDetail: { // FAQ详情
    method: 'POST',
    urlSuffix: '/getFaqDetail',
    errorMessage: 'Error fetching FAQ detail'
  },
  updateFaqCategoryStatus: { // FAQ分类状态修改
    method: 'POST',
    urlSuffix: '/updateFaqCategoryStatus',
    errorMessage: 'Error updating FAQ category status'
  }
}); 